import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ImageUploader from '../components/ui/image-uploader';
import BackupManager from '../components/BackupManager';
import { Plus, Building2, Trash2, Download, Archive, RotateCcw, Eraser } from 'lucide-react';
import { COMPANY_TYPES, COMPANY_TYPE_LABEL } from '../lib/modules';
import { downloadBlob } from '../lib/salespro';
import { toast } from 'sonner';

export default function Companies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    rif: '',
    fiscal_address: '',
    tax_id: '',
    phone: '',
    email: '',
    invoice_prefix: 'FAC',
    company_type: 'bienes'
  });

  const loadCompanies = async () => {
    try {
      // Master/superadmin pueden incluir inactivas
      const includeInactive = user?.role_id === 'master' || user?.role_id === 'superadmin';
      const response = await api.getCompanies(includeInactive);
      setCompanies(response.data);
    } catch (error) {
      toast.error('SalesPRO: Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => { await loadCompanies(); })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createCompany(formData);
      toast.success('Empresa creada');
      setDialogOpen(false);
      loadCompanies();
      setFormData({ name: '', logo_url: '', rif: '', fiscal_address: '', tax_id: '', phone: '', email: '', invoice_prefix: 'FAC', company_type: 'bienes' });
    } catch (error) {
      toast.error('Error al crear empresa');
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setEditDialogOpen(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await api.updateCompany(selectedCompany.company_id, {
        name: selectedCompany.name,
        logo_url: selectedCompany.logo_url || null,
        brand_name: selectedCompany.brand_name || null,
        warranty_text: selectedCompany.warranty_text || null,
        rif: selectedCompany.rif || null,
        fiscal_address: selectedCompany.fiscal_address || null,
        tax_id: selectedCompany.tax_id || null,
        phone: selectedCompany.phone || null,
        email: selectedCompany.email || null,
        invoice_prefix: selectedCompany.invoice_prefix || null,
        company_type: selectedCompany.company_type || null,
        invoice_counter: selectedCompany.invoice_counter != null ? parseInt(selectedCompany.invoice_counter) : null,
        delivery_note_prefix: selectedCompany.delivery_note_prefix || null,
        delivery_note_counter: selectedCompany.delivery_note_counter != null ? parseInt(selectedCompany.delivery_note_counter) : null,
        default_tax_rate: selectedCompany.default_tax_rate != null ? parseFloat(selectedCompany.default_tax_rate) : null,
      });
      if (selectedCompany.policy_clauses !== undefined) {
        try { await api.setCompanyClauses(selectedCompany.company_id, selectedCompany.policy_clauses || ''); } catch (err) { /* opcional */ }
      }
      toast.success('SalesPRO: Empresa actualizada');
      setEditDialogOpen(false);
      loadCompanies();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Error al actualizar empresa');
    }
  };

  const logoSrc = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : api.fileUrl(url);
  };

  const handleToggleActive = async (company) => {
    try {
      await api.updateCompany(company.company_id, {
        active: !company.active
      });
      toast.success(company.active ? 'SalesPRO: Empresa desactivada' : 'SalesPRO: Empresa activada');
      loadCompanies();
    } catch (error) {
      toast.error('SalesPRO: Error al cambiar estado de empresa');
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`SalesPRO: ¿Eliminar la empresa "${company.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.deleteCompany(company.company_id);
      toast.success('SalesPRO: Empresa eliminada');
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al eliminar empresa');
    }
  };

  const handleBackup = async (company) => {
    try {
      toast.info('SalesPRO: Generando respaldo...');
      const res = await api.companyBackup(company.company_id);
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename=([^;]+)/);
      downloadBlob(new Blob([res.data], { type: 'application/json' }), m ? m[1].trim() : `SalesPRO_respaldo_${company.name}.json`);
      toast.success('SalesPRO: Respaldo descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al generar respaldo');
    }
  };

  const handleFormat = async (company) => {
    const answer = window.prompt(`SalesPRO: Formatear "${company.name}" borrará TODOS los datos transaccionales (ventas, pagos, finanzas, notificaciones). Productos, clientes y usuarios se CONSERVAN.\n\nEscribe REINICIAR para confirmar:`);
    if (answer === null) return;
    if (answer !== 'REINICIAR') { toast.error('SalesPRO: Confirmación incorrecta'); return; }
    try {
      const res = await api.companyReset(company.company_id, 'REINICIAR');
      toast.success(res.data?.message || 'SalesPRO: Empresa formateada');
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al formatear empresa');
    }
  };

  const handleArchive = async (company) => {
    const answer = window.prompt(`SalesPRO: Archivar "${company.name}" la dejará en modo SOLO LECTURA (no operativa) y desactivará sus usuarios. Se descargará un respaldo de seguimiento.\n\nEscribe ARCHIVAR para confirmar:`);
    if (answer === null) return;
    if (answer !== 'ARCHIVAR') { toast.error('SalesPRO: Confirmación incorrecta'); return; }
    try {
      const res = await api.companyArchive(company.company_id, 'ARCHIVAR');
      const backup = res.data?.backup;
      if (backup) {
        downloadBlob(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }), `SalesPRO_respaldo_${(company.name || 'empresa').replace(/\s+/g, '_')}.json`);
      }
      toast.success(res.data?.message || 'SalesPRO: Empresa archivada');
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al archivar empresa');
    }
  };

  const handleRestore = async (company) => {
    if (!window.confirm(`SalesPRO: ¿Reactivar la empresa "${company.name}"?`)) return;
    try {
      const res = await api.companyRestore(company.company_id);
      toast.success(res.data?.message || 'SalesPRO: Empresa reactivada');
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al reactivar empresa');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="companies-loading">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Empresas</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestiona las empresas del sistema</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role_id === 'master' && (
            <Button
              variant="outline"
              onClick={() => setShowInactive(!showInactive)}
              data-testid="toggle-inactive-companies"
            >
              {showInactive ? 'Ocultar Inactivas' : 'Mostrar Inactivas'}
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-button" onClick={(e) => {
              // Bloqueamos la creación suelta: toda nueva empresa debe nacer desde "Vender Licencia".
              // Si es master, lo mandamos directo al flujo correcto; si no, no debería ver este botón.
              if (user?.role_id === 'master') {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = '/sell-license';
              }
            }}>
              <Plus size={18} className="mr-2" />
              Nueva Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="company-dialog">
            <DialogHeader>
              <DialogTitle>Nueva Empresa</DialogTitle>
              <p className="text-xs text-[#64748B] mt-1">Los datos fiscales aparecerán en las facturas SalesPRO emitidas por esta empresa.</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="name">Nombre de la Empresa *</Label>
                <Input
                  id="name"
                  data-testid="company-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Mi Empresa S.A."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>RIF</Label>
                  <Input
                    data-testid="company-rif-input"
                    value={formData.rif}
                    onChange={(e) => setFormData({ ...formData, rif: e.target.value })}
                    placeholder="J-401234567"
                  />
                </div>
                <div>
                  <Label>Prefijo factura</Label>
                  <Input
                    data-testid="company-prefix-input"
                    value={formData.invoice_prefix}
                    onChange={(e) => setFormData({ ...formData, invoice_prefix: e.target.value })}
                    placeholder="FAC"
                  />
                </div>
              </div>
              <div>
                <Label>Tipo de empresa</Label>
                <select
                  value={formData.company_type}
                  onChange={(e) => setFormData({ ...formData, company_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10"
                  data-testid="company-type-select"
                >
                  {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Dirección fiscal</Label>
                <Input
                  data-testid="company-fiscal-input"
                  value={formData.fiscal_address}
                  onChange={(e) => setFormData({ ...formData, fiscal_address: e.target.value })}
                  placeholder="Av. Principal, edificio X, Caracas"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    data-testid="company-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+58 212 1234567"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    data-testid="company-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contacto@empresa.com"
                  />
                </div>
              </div>
              <div>
                <ImageUploader
                  value={formData.logo_url}
                  onChange={(id) => setFormData({ ...formData, logo_url: id || '' })}
                  multiple={false}
                  purpose="company_logo"
                  label="Logo de la empresa"
                />
                <p className="text-xs text-[#94A3B8] mt-1">
                  Este logo aparecerá en todas las facturas y notas de entrega que emita la empresa.
                </p>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-company-button">
                Crear Empresa
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {user?.role_id === 'master' && <BackupManager />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies
          .filter(company => {
            // Master ve todas, otros solo activas
            if (user?.role_id === 'master') {
              return showInactive ? true : company.active;
            }
            return company.active;
          })
          .map((company) => (
          <Card key={company.company_id} className="border border-[#E2E8F0] shadow-sm" data-testid={`company-card-${company.company_id}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                {company.logo_url ? (
                  <img src={logoSrc(company.logo_url)} alt={company.name} className="w-12 h-12 rounded object-contain bg-white border border-[#E2E8F0]" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-12 h-12 bg-[#F1F5F9] rounded flex items-center justify-center">
                    <Building2 size={24} className="text-primary" />
                  </div>
                )}
                <div>
                  <CardTitle className="font-heading text-lg font-bold">{company.name}</CardTitle>
                  <p className="text-xs text-[#94A3B8]">{company.rif ? `RIF: ${company.rif}` : 'Sin RIF'}{company.invoice_prefix ? ` · ${company.invoice_prefix}-${String((company.invoice_counter || 0) + 1).padStart(6, '0')}` : ''}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700" data-testid={`company-type-badge-${company.company_id}`}>
                    {COMPANY_TYPE_LABEL[company.company_type] || 'Bienes'}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Contadores de documentos por empresa */}
                <div className="grid grid-cols-3 gap-2 text-center bg-[#F8FAFC] rounded-md p-2 border border-[#E2E8F0]" data-testid={`company-counters-${company.company_id}`}>
                  <div>
                    <p className="text-[10px] uppercase text-[#94A3B8]">Próx. Factura</p>
                    <p className="text-xs font-bold text-[#022C22]">{company.invoice_prefix || 'FAC'}-{String((company.invoice_counter || 0) + 1).padStart(6, '0')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#94A3B8]">Próx. Nota</p>
                    <p className="text-xs font-bold text-[#022C22]">{company.delivery_note_prefix || 'NE'}-{String((company.delivery_note_counter || 0) + 1).padStart(6, '0')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#94A3B8]">IVA</p>
                    <p className="text-xs font-bold text-[#022C22]">{company.default_tax_rate ?? 16}%</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    company.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {company.active ? 'Activa' : 'Inactiva'}
                  </span>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEditCompany(company)}
                      data-testid={`configure-company-${company.company_id}`}
                    >
                      Configurar
                    </Button>
                    {user?.role_id === 'master' && (
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => handleDelete(company)}
                        data-testid={`delete-company-${company.company_id}`}
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
                  <span className="text-sm text-[#64748B]">
                    {company.active ? 'Desactivar empresa' : 'Activar empresa'}
                  </span>
                  <button
                    onClick={() => handleToggleActive(company)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      company.active ? 'bg-primary' : 'bg-gray-300'
                    }`}
                    data-testid={`toggle-company-${company.company_id}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        company.active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Respaldo / formateo / archivo (solo Master) */}
                {user?.role_id === 'master' && (
                  <div className="pt-2 border-t border-[#E2E8F0] space-y-2" data-testid={`company-admin-actions-${company.company_id}`}>
                    {company.archived && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">
                        <Archive size={11} /> Archivada · solo lectura
                      </span>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => handleBackup(company)} data-testid={`backup-company-${company.company_id}`}>
                        <Download size={13} className="mr-1.5" /> Respaldo
                      </Button>
                      {company.archived ? (
                        <Button size="sm" variant="outline" className="text-xs text-green-700 border-green-200 hover:bg-green-50" onClick={() => handleRestore(company)} data-testid={`restore-company-${company.company_id}`}>
                          <RotateCcw size={13} className="mr-1.5" /> Reactivar
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="text-xs text-orange-700 border-orange-200 hover:bg-orange-50" onClick={() => handleFormat(company)} data-testid={`format-company-${company.company_id}`}>
                            <Eraser size={13} className="mr-1.5" /> Formatear
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs text-amber-700 border-amber-200 hover:bg-amber-50 col-span-2" onClick={() => handleArchive(company)} data-testid={`archive-company-${company.company_id}`}>
                            <Archive size={13} className="mr-1.5" /> Archivar (muerta)
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {companies.length === 0 && (
        <div className="text-center py-12" data-testid="no-companies-message">
          <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No hay empresas registradas</p>
        </div>
      )}

      {/* Edit Company Dialog */}
      {selectedCompany && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-company-dialog">
            <DialogHeader>
              <DialogTitle>Configurar Empresa</DialogTitle>
              <p className="text-xs text-[#64748B] mt-1">Datos fiscales y correlativos que aparecerán en las facturas SalesPRO.</p>
            </DialogHeader>
            <form onSubmit={handleUpdateCompany} className="space-y-3">
              <div>
                <Label>Nombre / Razón social *</Label>
                <Input
                  value={selectedCompany.name}
                  onChange={(e) => setSelectedCompany({...selectedCompany, name: e.target.value})}
                  required
                  data-testid="edit-company-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>RIF</Label>
                  <Input value={selectedCompany.rif || ''} onChange={(e) => setSelectedCompany({...selectedCompany, rif: e.target.value})} placeholder="J-401234567" data-testid="edit-company-rif" />
                </div>
                <div>
                  <Label>NIT / ID fiscal</Label>
                  <Input value={selectedCompany.tax_id || ''} onChange={(e) => setSelectedCompany({...selectedCompany, tax_id: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Dirección fiscal</Label>
                <Input value={selectedCompany.fiscal_address || ''} onChange={(e) => setSelectedCompany({...selectedCompany, fiscal_address: e.target.value})} placeholder="Av. Principal, Caracas" />
              </div>
              <div className="rounded-md border border-[#E2E8F0] p-3 bg-[#F8FAFC] space-y-2">
                <p className="text-xs font-semibold text-[#022C22]">Marca en documentos (licencia white-label)</p>
                <div>
                  <Label>Nombre de marca (sale en recibos/facturas)</Label>
                  <Input value={selectedCompany.brand_name || ''} onChange={(e) => setSelectedCompany({...selectedCompany, brand_name: e.target.value})} placeholder="Ej. CELULAR BEACH" data-testid="edit-company-brandname" />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Si lo dejas vacío, se usa "CELULAR BEACH".</p>
                </div>
                <div>
                  <Label>Nota de garantía (definida por la tienda, opcional)</Label>
                  <Input value={selectedCompany.warranty_text || ''} onChange={(e) => setSelectedCompany({...selectedCompany, warranty_text: e.target.value})} placeholder="Ej. 3 meses por defectos de fábrica" data-testid="edit-company-warranty" />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Aparecerá en los recibos de venta de equipos. La define la empresa que usa la licencia.</p>
                </div>
                <div>
                  <Label>Cláusulas legales propias de la empresa (opcional)</Label>
                  <textarea
                    value={selectedCompany.policy_clauses || ''}
                    onChange={(e) => setSelectedCompany({ ...selectedCompany, policy_clauses: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                    placeholder="Cláusulas adicionales que se suman a la base legal del sistema (privacidad, resguardo, IA)."
                    data-testid="edit-company-clauses"
                  />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Se añaden a la base legal robusta del sistema (que nos protege). No reemplazan la base; solo agregan términos propios de esta empresa.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Teléfono</Label>
                  <Input value={selectedCompany.phone || ''} onChange={(e) => setSelectedCompany({...selectedCompany, phone: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={selectedCompany.email || ''} onChange={(e) => setSelectedCompany({...selectedCompany, email: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Tipo de empresa</Label>
                <select
                  value={selectedCompany.company_type || 'bienes'}
                  onChange={(e) => setSelectedCompany({ ...selectedCompany, company_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-10"
                  data-testid="edit-company-type"
                >
                  {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prefijo factura</Label>
                  <Input value={selectedCompany.invoice_prefix || 'FAC'} onChange={(e) => setSelectedCompany({...selectedCompany, invoice_prefix: e.target.value})} placeholder="FAC" data-testid="edit-company-prefix" />
                </div>
                <div>
                  <Label>Correlativo factura</Label>
                  <Input type="number" value={selectedCompany.invoice_counter ?? 0} onChange={(e) => setSelectedCompany({...selectedCompany, invoice_counter: e.target.value})} data-testid="edit-company-counter" />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Próxima: {selectedCompany.invoice_prefix || 'FAC'}-{String((parseInt(selectedCompany.invoice_counter) || 0) + 1).padStart(6, '0')}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prefijo nota entrega</Label>
                  <Input value={selectedCompany.delivery_note_prefix || 'NE'} onChange={(e) => setSelectedCompany({...selectedCompany, delivery_note_prefix: e.target.value})} placeholder="NE" data-testid="edit-company-ne-prefix" />
                </div>
                <div>
                  <Label>Correlativo nota</Label>
                  <Input type="number" value={selectedCompany.delivery_note_counter ?? 0} onChange={(e) => setSelectedCompany({...selectedCompany, delivery_note_counter: e.target.value})} data-testid="edit-company-ne-counter" />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Próxima: {selectedCompany.delivery_note_prefix || 'NE'}-{String((parseInt(selectedCompany.delivery_note_counter) || 0) + 1).padStart(6, '0')}</p>
                </div>
              </div>
              <div>
                <Label>IVA por defecto (%)</Label>
                <Input type="number" step="0.01" value={selectedCompany.default_tax_rate ?? 16} onChange={(e) => setSelectedCompany({...selectedCompany, default_tax_rate: e.target.value})} data-testid="edit-company-tax" />
              </div>
              <div>
                <ImageUploader
                  value={selectedCompany.logo_url}
                  onChange={(id) => setSelectedCompany({...selectedCompany, logo_url: id || ''})}
                  multiple={false}
                  purpose="company_logo"
                  label="Logo de la empresa"
                />
                <p className="text-xs text-[#94A3B8] mt-1">Sube el logo desde tu dispositivo. Aparecerá en facturas y notas de entrega.</p>
              </div>
              <Button type="submit" className="w-full" data-testid="save-company-button">
                Guardar Cambios
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}